// The RFP setup card on the Overview (Lane 1440, Ryan's mockup v1). One RFP title for the
// buyer and every supplier, the planned launch date, offers due, rounds and award by. It
// takes the Schedule card's place: Rounds and Award by open today's Schedule settings, and
// the open round keeps its menu. Title, launch date and offers due save when the buyer
// leaves the field, through the workspace's one save queue ("Launch" waits for it).
import React, { useEffect, useRef, useState } from 'react';
import { formatDate } from './sourcingApi';
import { formatDueAt } from '../sourcing-portal/portalView';
import { toLocalInput } from './RoundTab';
import { viewerZone } from './useSourcingChat';

// The saved offers due as the field shows it: the saved instant, else the saved day at
// 5 PM in this browser (Review and publish's own default, PublishReview.savedValues).
export function offersDueLocal(due) {
  if (!due) return '';
  if (due.due_at) return toLocalInput(due.due_at);
  return due.due_date ? `${String(due.due_date).slice(0, 10)}T17:00` : '';
}

export function offersDueText(due) {
  if (!due || !due.due_date) return '';
  if (due.due_at) return formatDueAt(due.due_at);
  return `${formatDate(due.due_date)}, time not set (5:00 PM)`;
}

function TitleFlag({ suggestion, names = [], onUse }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const btn = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const key = (e) => { if (e.key === 'Escape') { setOpen(false); if (btn.current) btn.current.focus(); } };
    const away = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('keydown', key);
    document.addEventListener('mousedown', away);
    return () => { document.removeEventListener('keydown', key); document.removeEventListener('mousedown', away); };
  }, [open]);
  return (
    <span className="srce-dd srce-setup-flagwrap" ref={wrap}>
      <button type="button" ref={btn} className="srce-setup-flag" aria-haspopup="dialog" aria-expanded={open}
        aria-label="The title names one of your suppliers" title="The title names one of your suppliers"
        onClick={() => setOpen(!open)}>!</button>
      {open && (
        <span className="srce-ddpanel srce-setup-pop" role="dialog" aria-label="Title names a supplier">
          <span>This title names one of your suppliers{names.length ? ` (${names.join(', ')})` : ''}, and every supplier will see it.</span>
          <span>Change it to: <span className="srce-strong">{suggestion}</span></span>
          <span className="srce-row">
            <button type="button" className="srce-btn srce-btn--primary srce-btn--sm"
              onClick={() => { setOpen(false); onUse(suggestion); }}>Use this</button>
            <button type="button" className="ll-clr" onClick={() => setOpen(false)}>Keep my title</button>
          </span>
        </span>
      )}
    </span>
  );
}

// A value with "Change"; while changing, one field that saves when the buyer leaves it
// (Enter too) and Escape puts back.
function InlineField({ id, label, type, value, display, empty, onSave, editing, setEditing, extra }) {
  const [text, setText] = useState(value);
  useEffect(() => { if (!editing) setText(value); }, [value, editing]);
  const done = (save) => {
    setEditing(false);
    if (save && text !== value) onSave(text);
  };
  return (
    <div className="srce-setup-r">
      <label className="srce-setup-k" htmlFor={id}>{label}</label>
      {editing ? (
        <input id={id} className="srce-input srce-setup-in" type={type} value={text} autoFocus
          onChange={(e) => setText(e.target.value)} onBlur={() => done(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); done(true); }
            if (e.key === 'Escape') { setText(value); setEditing(false); }
          }} />
      ) : (
        <span className={`srce-setup-v srce-ell${display ? '' : ' srce-mut'}`} title={display || empty}>{display || empty}</span>
      )}
      {extra}
      {!editing && (
        <button type="button" className="ll-clr srce-setup-act" aria-label={`${display ? 'Change' : 'Set'} ${label.toLowerCase()}`}
          onClick={() => setEditing(true)}>{display ? 'Change' : 'Set'}</button>
      )}
    </div>
  );
}

export default function RfpSetupCard({ api, eventId, data, saveQueue, onChanged, onOpenSchedule, roundControl,
  editDueAt }) {
  const setup = data.invitations.setup;
  const draft = data.status === 'draft';
  const [title, setTitle] = useState(setup.title);
  const titleDirty = useRef(false);
  const [editingLaunch, setEditingLaunch] = useState(false);
  const [editingDue, setEditingDue] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { if (!titleDirty.current) setTitle(setup.title); }, [setup.title]);
  // "Set offers due" from the Next step line opens the field.
  useEffect(() => { if (editDueAt && draft) setEditingDue(true); }, [editDueAt, draft]);

  // A change with the event's revision; when another save moved it first (a mark, a
  // contact), the newest revision is read and the change sent once more.
  const withRevision = async (fn) => {
    try {
      await fn(data.revision);
    } catch (e) {
      if (e.code !== 'stale_revision') throw e;
      const fresh = await api.event(eventId);
      await fn(fresh.revision);
    }
  };
  const save = (key, fn) => {
    setError('');
    return saveQueue.run(key, async () => {
      try {
        await fn();
      } catch (e) {
        setError(e.message);
        throw e;
      }
      if (onChanged) await onChanged();
    });
  };
  const saveTitle = (next) => {
    const name = (next || '').trim();
    titleDirty.current = false;
    if (!name || name === setup.title) { setTitle(setup.title); return; }
    setTitle(name);
    save('title', () => withRevision((rev) => api.act(eventId, 'PATCH', '', { name }, rev)));
  };
  const canEditTitle = setup.title_editable;
  const launchText = draft ? (setup.launch_date ? formatDate(setup.launch_date) : '')
    : (setup.launched_at ? `Launched ${formatDueAt(setup.launched_at)}` : '');
  const due = setup.offers_due;
  const currentRound = (data.rounds || []).find((r) => r.round === data.current_round);
  const rounds = data.schedule && data.schedule.planned_rounds;
  const roundsText = draft ? (rounds ? `Up to ${rounds}` : 'One round')
    : `Round ${data.current_round || 1}${rounds && rounds >= (data.current_round || 1) ? ` of ${rounds}` : ''}`;
  const status = saveQueue.pending > 0 ? 'Saving…' : saveQueue.error ? '' : 'Saved';

  return (
    <section className="srce-card2" data-testid="srce-rfp-setup">
      <div className="srce-card2-h">
        <span className="srce-card2-t">RFP setup</span>
        <span className="srce-mut srce-push srce-setup-status" role="status">{status}</span>
      </div>
      {(error || saveQueue.error) && <div className="srce-error srce-card2-f" role="alert">Not saved: {error || saveQueue.error}</div>}
      <div className="srce-setup">
        <div className="srce-setup-r">
          <span className="srce-setup-k srce-row">
            <label htmlFor="srce-setup-title">RFP title</label>
            {setup.title_flag && <TitleFlag suggestion={setup.title_suggestion} names={setup.title_names || []} onUse={saveTitle} />}
          </span>
          {canEditTitle ? (
            <input id="srce-setup-title" className="srce-input srce-setup-in" value={title} maxLength={255}
              onChange={(e) => { titleDirty.current = true; setTitle(e.target.value); }}
              onBlur={() => { if (titleDirty.current) saveTitle(title); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                if (e.key === 'Escape') { titleDirty.current = false; setTitle(setup.title); }
              }} />
          ) : <span className="srce-setup-v srce-ell" title={setup.title}>{setup.title}</span>}
        </div>
        {setup.suppliers_see && (
          <div className="srce-setup-r srce-mut">
            <span className="srce-setup-k">Suppliers see</span>
            <span className="srce-setup-v srce-ell" title={setup.suppliers_see}>{setup.suppliers_see}</span>
          </div>
        )}
        {draft ? (
          <InlineField id="srce-setup-launch" label="Launch date" type="date" value={setup.launch_date || ''}
            display={launchText} empty="Not set" editing={editingLaunch} setEditing={setEditingLaunch}
            extra={setup.launch_conflict ? <span className="srce-pill srce-pill--warn" title="The launch date is after offers due">Needs updating</span> : null}
            onSave={(v) => save('launch', () => withRevision((rev) => api.setLaunchDate(eventId, v || null, rev)))} />
        ) : (
          <div className="srce-setup-r">
            <span className="srce-setup-k">Launch date</span>
            <span className="srce-setup-v srce-ell">{launchText || 'Not set'}</span>
          </div>
        )}
        {draft ? (
          <InlineField id="srce-setup-due" label="Offers due" type="datetime-local" value={offersDueLocal(due)}
            display={offersDueText(due)} empty="Not set" editing={editingDue} setEditing={setEditingDue}
            onSave={(v) => {
              const d = v ? new Date(v) : null;
              if (v && (!d || Number.isNaN(d.getTime()))) return;
              const zone = viewerZone();
              save('due', () => api.savePublishDraft(eventId, v
                ? { due_at: d.toISOString(), ...(zone ? { time_zone: zone } : {}) } : { due_at: '' }));
            }} />
        ) : (
          <div className="srce-setup-r">
            <span className="srce-setup-k">Offers due</span>
            <span className="srce-setup-v srce-ell">{currentRound && currentRound.due_at
              ? `Round ${currentRound.round}: ${formatDueAt(currentRound.due_at)}` : 'Not set'}</span>
          </div>
        )}
        <div className="srce-setup-r">
          <span className="srce-setup-k">Rounds</span>
          <span className="srce-setup-v srce-ell">{roundsText}</span>
          {roundControl}
          {data.schedule && data.schedule.can_edit && (
            <button type="button" className="ll-clr srce-setup-act" onClick={onOpenSchedule} aria-label="Change the rounds">Change</button>)}
        </div>
        <div className="srce-setup-r">
          <span className="srce-setup-k">Award by</span>
          <span className={`srce-setup-v srce-ell${setup.award_by ? '' : ' srce-mut'}`}>{setup.award_by ? formatDate(setup.award_by) : 'Not set'}</span>
          {data.schedule && data.schedule.can_edit && (
            <button type="button" className="ll-clr srce-setup-act" onClick={onOpenSchedule}
              aria-label={`${setup.award_by ? 'Change' : 'Set'} the award date`}>{setup.award_by ? 'Change' : 'Set'}</button>)}
        </div>
      </div>
    </section>
  );
}
